'use client';

// /subscribe?tier=hobby|build|scale|business — focused page for
// starting a subscription. /pricing tier cards link here with the
// chosen tier already selected; the customer picks term + payment
// method and the server quotes a deposit.
//
// Logged-out behaviour: redirect to /signup with the tier preserved
// in a query string (?next=/subscribe&tier=...) so the user can
// resume after creating an identity. /signup picks up `next` post-
// account-creation.

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import {
  ApiCallFailed,
  createPayment,
  getAccount,
  type AccountDto,
  type PaymentMethod,
  type SubscriptionTerm,
  type TierName,
} from '../../lib/api';
import { loadIdentity, type Identity } from '../../lib/identity';

// Tier strict-ordering: indices 0..5. Used to decide whether
// target=X over current=Y is a sideways move, an upgrade, or a
// downgrade. trial counts as the "no subscription yet" floor; the
// server resolves trial→hobby as a fresh subscribe, not an upgrade.
const TIER_ORDER: TierName[] = ['trial', 'hobby', 'build', 'scale', 'business', 'dedicated'];
const tierIndex = (t: TierName): number => TIER_ORDER.indexOf(t);

const VALID_TIERS: TierName[] = ['hobby', 'build', 'scale', 'business'];
const TIER_BLURBS: Record<TierName, string> = {
  trial:      'Free read-only access for evaluation. Not paid.',
  hobby:      'Entry tier — accessible enough to keep around as a backup endpoint.',
  build:      'Production small-app workloads. Per-token method scope and RPS caps.',
  scale:      'Volume sweet spot. Per-token sub-budgets.',
  business:   'Commit volume + tighter SLA + dedicated capacity hints.',
  dedicated:  'Reserved capacity. Talk to support.',
};

const PAYMENT_METHODS: { value: PaymentMethod; label: string; hint: string }[] = [
  { value: 'bch',  label: 'BCH',  hint: 'Native, fastest confirmation' },
  { value: 'pusd', label: 'PUSD', hint: 'ParyonUSD CashToken — price-stable' },
  { value: 'musd', label: 'MUSD', hint: 'Moria USD CashToken — price-stable' },
];

type Step =
  | { kind: 'loading' }
  | { kind: 'configuring' }
  | { kind: 'submitting' }      // briefly while POST /payment-request is in-flight; on success we redirect
  | { kind: 'noop'; message: string }
  | { kind: 'error'; message: string };

export default function SubscribePage() {
  return (
    <Suspense fallback={
      <main className="container-x py-24 text-sm text-ink-dim text-center">Loading…</main>
    }>
      <SubscribePageInner />
    </Suspense>
  );
}

function SubscribePageInner() {
  const router = useRouter();
  const search = useSearchParams();
  const tier_q = search?.get('tier') ?? null;

  const [identity, setIdentity] = useState<Identity | null>(null);
  const [account,  setAccount]  = useState<AccountDto | null>(null);
  const [step, setStep]         = useState<Step>({ kind: 'loading' });
  const [term, setTerm]         = useState<SubscriptionTerm>('monthly');
  const [method, setMethod]     = useState<PaymentMethod>('bch');

  // Resolve tier from query, gate the page if invalid, redirect to
  // /signup if the user isn't holding an identity yet, and load
  // account state so we can pick the right purpose (subscribe vs
  // upgrade) and detect downgrade attempts client-side.
  useEffect(() => {
    let cancelled = false;
    if (!tier_q || !VALID_TIERS.includes(tier_q as TierName)) {
      router.replace('/pricing');
      return;
    }
    (async () => {
      try {
        const id = await loadIdentity();
        if (cancelled) return;
        if (!id) {
          router.replace(`/signup?next=/subscribe%3Ftier%3D${tier_q}`);
          return;
        }
        setIdentity(id);
        const acc = await getAccount(id);
        if (cancelled) return;
        setAccount(acc);
        setStep({ kind: 'configuring' });
      } catch (err) {
        if (!cancelled) setStep({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => { cancelled = true; };
  }, [tier_q, router]);

  // Effective purpose: subscribe for trial / never-paid accounts;
  // upgrade for paid accounts moving up the ladder. Server has the
  // final say on what's allowed; this just picks the friendlier
  // wire payload.
  function effectivePurpose(): 'subscribe' | 'upgrade' {
    if (!account) return 'subscribe';
    if (account.tier === 'trial' || account.first_payment_at === null) return 'subscribe';
    return 'upgrade';
  }

  // Direction-of-move classifier for the "current → target" hint.
  // null when account isn't loaded yet.
  function direction(): 'new' | 'up' | 'same' | 'down' | null {
    if (!tier_q || !account) return null;
    if (account.tier === 'trial' || account.first_payment_at === null) return 'new';
    const cur = tierIndex(account.tier as TierName);
    const tgt = tierIndex(tier_q as TierName);
    if (cur === tgt) return 'same';
    return tgt > cur ? 'up' : 'down';
  }

  async function submit() {
    if (!identity || !tier_q) return;
    setStep({ kind: 'submitting' });
    try {
      const res = await createPayment({
        account_id:     identity.account_id,
        purpose:        effectivePurpose(),
        target_tier:    tier_q as TierName,
        target_term:    term,
        payment_method: method,
      });
      if ('payment_request_id' in res) {
        // Each quote gets its own permalink at /payment-request/<id>;
        // the deposit card lives there with countdown + QR + abandon.
        // /subscribe stays as the configuration page only.
        router.push(`/payment-request/${res.payment_request_id}`);
      } else {
        setStep({ kind: 'noop', message: res.message });
      }
    } catch (err) {
      console.error('[subscribe/submit] threw:', err);
      setStep({ kind: 'error', message: friendlySubscribeError(err) });
    }
  }

  if (step.kind === 'loading') {
    return <main className="container-x py-24 text-sm text-ink-dim text-center">Checking identity…</main>;
  }

  const tier = tier_q as TierName;
  const dir  = direction();
  const heading = dir === 'up' ? 'Upgrade' : dir === 'same' ? 'Already on' : 'Subscribe';

  return (
    <main className="container-x py-12 max-w-2xl space-y-8">
      <header>
        <Link href="/pricing" className="text-xs text-ink-faint hover:text-accent">← Back to pricing</Link>
        <h1 className="heading-1 mt-3 capitalize">{heading} — {tier}</h1>
        {dir === 'up' && account && (
          <p className="mt-3 text-sm text-ink-dim">
            Currently on <span className="text-ink capitalize">{account.tier}</span>;
            moving up to <span className="text-ink capitalize">{tier}</span>.
            Server pro-rates the remaining cycle's already-paid CC against the new tier rate.
          </p>
        )}
        <p className="text-lead mt-3">{TIER_BLURBS[tier]}</p>
      </header>

      {dir === 'same' && (
        <section className="card text-sm text-ink-dim">
          You're already on the {tier} tier. Pick a different tier from{' '}
          <Link href="/pricing" className="text-accent hover:text-accent-muted">pricing</Link>{' '}
          to upgrade, or head to the{' '}
          <Link href="/dashboard" className="text-accent hover:text-accent-muted">dashboard</Link>{' '}
          to top up CC at your existing rate.
        </section>
      )}

      {dir === 'down' && (
        <section className="card text-sm text-yellow-200 bg-yellow-500/10 ring-1 ring-inset ring-yellow-500/30">
          {tier} is below your current tier ({account?.tier}). Downgrades take effect at the next
          cycle boundary; that flow isn't wired into the dashboard yet — contact support to schedule.
        </section>
      )}

      {step.kind === 'configuring' && dir !== 'same' && dir !== 'down' && (
        <section className="card space-y-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-faint mb-2">Term</div>
            <div className="flex flex-wrap gap-2">
              <PickButton active={term === 'monthly'} onClick={() => setTerm('monthly')}>
                Monthly
              </PickButton>
              <PickButton active={term === 'annual'} onClick={() => setTerm('annual')}>
                Annual <span className="text-ink-faint">(~17% off)</span>
              </PickButton>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-ink-faint mb-2">Pay with</div>
            <div className="flex flex-wrap gap-2">
              {PAYMENT_METHODS.map((m) => (
                <PickButton key={m.value} active={method === m.value} onClick={() => setMethod(m.value)} title={m.hint}>
                  {m.label}
                </PickButton>
              ))}
            </div>
            <p className="mt-2 text-xs text-ink-faint">
              {PAYMENT_METHODS.find(p => p.value === method)?.hint}
            </p>
          </div>

          <button onClick={submit} className="btn-primary text-sm">
            Quote subscription
          </button>
        </section>
      )}

      {step.kind === 'submitting' && (
        <section className="card text-sm text-ink-dim">Quoting…</section>
      )}

      {step.kind === 'noop' && (
        <section className="card">
          <div className="rounded-md bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 p-4 text-sm text-emerald-200">
            {step.message}
          </div>
          <Link href="/dashboard" className="btn-primary text-sm mt-6 inline-flex">
            Go to dashboard
          </Link>
        </section>
      )}

      {step.kind === 'error' && (
        <section className="card">
          <div className="rounded-md bg-red-500/10 ring-1 ring-inset ring-red-500/30 p-4 text-sm text-red-200">
            {step.message}
          </div>
          <button onClick={() => setStep({ kind: 'configuring' })} className="btn-secondary text-sm mt-6">
            Try again
          </button>
        </section>
      )}
    </main>
  );
}

function friendlySubscribeError(err: unknown): string {
  if (!(err instanceof ApiCallFailed)) return (err as Error).message;
  const body = err.body as { error?: string; reason?: string };
  // Accept both the current `payments_not_enabled` and the legacy
  // `xpub_not_provisioned` reason during the rollover window.
  if (err.status === 503 &&
      (body.reason === 'payments_not_enabled' || body.reason === 'xpub_not_provisioned')) {
    return "Subscriptions aren't enabled on this server yet.";
  }
  if (err.status === 503 && (body.reason === 'price_feed_unavailable' || (body.error ?? '').includes('price_feed'))) {
    return 'FX price feed temporarily unavailable. Retry in a minute.';
  }
  // Pre-fix legacy 400 path: a server-side state sentinel wrapped as
  // a generic db_error → 400 in older releases. Now mapped to
  // xpub_not_provisioned → 503, but keep this branch for resilience
  // against an older API still running upstream.
  if ((body.error ?? '').includes('hd_wallet_state singleton row missing')) {
    return "Subscriptions aren't enabled on this server yet.";
  }
  return body.error ?? `${err.status}: request failed`;
}

function PickButton({ active, onClick, children, title }: {
  active: boolean; onClick: () => void; children: React.ReactNode; title?: string;
}) {
  return (
    <button onClick={onClick} title={title}
            className={`px-4 py-2 text-sm rounded-md ring-1 ring-inset transition ${
              active
                ? 'bg-accent/15 text-accent ring-accent/30'
                : 'text-ink-dim ring-white/10 hover:bg-surface-2'
            }`}>
      {children}
    </button>
  );
}

